import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { toast } from "sonner";
import { ChevronLeft, Ribbon, ShieldCheck, Clock, XCircle, Upload } from "lucide-react";

export default function BecomeCoach() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  usePageMeta(t("become_coach_title"));
  const [v, setV] = useState(undefined);
  const [dpaAccepted, setDpaAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/coach/verify/status"); setV(data?.verification || { status: "unverified" }); setDpaAccepted(!!data?.verification?.dpa_accepted); }
    catch { setV({ status: "unverified" }); }
  };
  useEffect(() => { load(); }, []);

  const acceptDpa = async () => {
    setBusy(true);
    try { await api.post("/coach/verify/accept-dpa", { accepted: true, version: "2026-07" }); setDpaAccepted(true); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1] || "");
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const { data } = await api.post("/coach/verify/upload", { image_base64: b64, file_name: file.name, mime_type: file.type || "image/jpeg" });
      toast.success(t("popup_submitted"));
      if (data?.verification) setV(data.verification); else await load();
      await refreshUser();
    } catch (err) { toast.error(apiError(err) || t("popup_couldnt_read_image")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const Header = () => (
    <div className="flex items-center gap-3">
      <button onClick={() => navigate("/app/account")} data-testid="coach-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
      <h1 className="font-display text-2xl text-foreground">{t("become_coach_title")}</h1>
    </div>
  );

  if (user?.is_anon) {
    return (
      <div className="mx-auto max-w-md space-y-5" data-testid="become-coach-screen">
        <Header />
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-8 text-center">
          <Ribbon className="h-8 w-8 text-primary" />
          <p className="font-display text-lg text-foreground">{t("guest_cta_title")}</p>
          <p className="text-sm text-muted-foreground">{t("guest_cta_body")}</p>
          <button onClick={() => navigate("/app/credits")} data-testid="coach-guest-cta" className="mt-1 rounded-full bg-primary px-6 py-2.5 font-semibold text-primary-foreground">{t("create_account_cta")}</button>
        </div>
      </div>
    );
  }

  const status = v?.status || "unverified";

  return (
    <div className="mx-auto max-w-md space-y-5" data-testid="become-coach-screen">
      <Header />
      {v === undefined ? <Spinner label={t("common.loading")} /> : status === "verified" ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-8 text-center" data-testid="coach-verified">
          <ShieldCheck className="h-9 w-9 text-emerald-600" />
          <p className="font-display text-lg text-foreground">{t("become_coach_full_access")}</p>
          <p className="text-sm text-muted-foreground">{t("once_verified")}</p>
        </div>
      ) : status === "pending_admin" || status === "pending" || status === "pending_ai" ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-8 text-center" data-testid="coach-pending">
          <Clock className="h-9 w-9 text-amber-600" />
          <p className="font-display text-lg text-foreground">{t("not_yet_verified")}</p>
          <p className="text-sm text-muted-foreground">{t("qual_cert_before_verified")}</p>
          {v?.ai_verdict ? <p className="text-xs italic text-muted-foreground">{t("ai_verdict")}: {v.ai_verdict}</p> : null}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5">
            <p className="font-display text-base text-foreground">{t("become_coach_complete_steps")}</p>
          </div>

          {status === "rejected" ? (
            <div className="flex items-center gap-2 rounded-2xl border border-rose-300 bg-rose-50 p-3" data-testid="coach-rejected">
              <XCircle className="h-5 w-5 text-rose-600" />
              <p className="text-sm text-foreground">{t("become_coach_rejected")}</p>
            </div>
          ) : null}

          {!dpaAccepted ? (
            <div className="space-y-3 rounded-2xl border border-border bg-card p-5">
              <p className="font-display text-base text-foreground">{t("dpa_title")}</p>
              <p className="text-sm leading-relaxed text-muted-foreground">{t("dpa_body")}</p>
              <p className="text-xs text-muted-foreground">{t("dpa_joint_controllers")}</p>
              <button onClick={acceptDpa} disabled={busy} data-testid="coach-dpa-accept" className="w-full rounded-full bg-primary py-2.5 font-semibold text-primary-foreground disabled:opacity-50">{t("dpa_accept_button")}</button>
            </div>
          ) : (
            <div className="space-y-3 rounded-2xl border border-border bg-card p-5">
              <p className="font-display text-base text-foreground">{t("qual_cert_title")}</p>
              <p className="text-sm text-muted-foreground">{t("qual_cert_hint")}</p>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" data-testid="coach-cert-file" />
              <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="coach-cert-upload" className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2.5 font-semibold text-primary-foreground disabled:opacity-50">
                {busy ? <Spinner /> : <><Upload className="h-4 w-4" /> {status === "rejected" ? t("qual_cert_reupload") : t("qual_cert_upload")}</>}
              </button>
              <p className="text-[11px] text-muted-foreground">{t("qual_cert_areas")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
